import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToSection } from "./Navbar";

const TYPES = [
  { id: "vetrina", label: "Sito Vetrina", extra: 0 },
  { id: "blog", label: "Sito + Blog", extra: 300 },
  { id: "ecommerce", label: "E-commerce", extra: 900 },
];
const SEO = [
  { id: "none", label: "Nessuna SEO", monthly: 0 },
  { id: "base", label: "SEO Base", monthly: 70 },
  { id: "advanced", label: "SEO Avanzata", monthly: 160 },
];
const UPDATES = [
  { id: "standard", label: "Aggiornamenti standard", monthly: 0 },
  { id: "priority", label: "Assistenza prioritaria", monthly: 20 },
];

export default function PriceCalculator() {
  const [type, setType] = useState("vetrina");
  const [pages, setPages] = useState(5);
  const [seo, setSeo] = useState("base");
  const [updates, setUpdates] = useState("standard");

  const t = TYPES.find((x) => x.id === type);
  const s = SEO.find((x) => x.id === seo);
  const u = UPDATES.find((x) => x.id === updates);
  const oneTime = 690 + (pages - 3) * 80 + t.extra;
  const monthly = 29 + s.monthly + u.monthly;

  const request = () => {
    sessionStorage.setItem(
      "preventivo",
      `Preventivo dal calcolatore interattivo: ${t.label}, ${pages} pagine, ${s.label}, ${u.label}. Stima: €${oneTime} una tantum + €${monthly}/mese. Vorrei essere ricontattato per i dettagli.`
    );
    window.dispatchEvent(new Event("preventivo:set"));
    scrollToSection("contatti");
  };

  const chip = (active) =>
    `rounded-full px-4 py-2 text-xs sm:text-sm font-medium border transition-all duration-300 ${
      active
        ? "bg-[#E05638] border-[#E05638] text-white"
        : "bg-white/5 border-white/15 text-neutral-300 hover:border-white/50"
    }`;

  return (
    <section data-testid="price-calculator" className="pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl bg-[#1E293B] text-white p-8 sm:p-12 lg:p-14 grid lg:grid-cols-2 gap-12 shadow-[0_40px_80px_-20px_rgba(30,41,59,0.45)]"
        >
          <div>
            <p className="mono-label text-[#E05638] mb-4">Calcolatore interattivo</p>
            <h3 className="font-display font-bold tracking-tight text-2xl sm:text-3xl leading-tight">
              Componi il tuo preventivo in tempo reale
            </h3>

            <p className="mono-label text-neutral-400 mt-10 mb-3">Tipo di sito</p>
            <div className="flex flex-wrap gap-2.5">
              {TYPES.map((x) => (
                <button key={x.id} data-testid={`calc-type-${x.id}`} onClick={() => setType(x.id)} className={chip(type === x.id)}>
                  {x.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mt-9 mb-3">
              <p className="mono-label text-neutral-400">Numero di pagine</p>
              <span data-testid="calc-pages-value" className="font-display font-bold text-lg text-[#E05638]">{pages}</span>
            </div>
            <input
              data-testid="calc-pages-slider"
              type="range"
              min={3}
              max={20}
              value={pages}
              onChange={(e) => setPages(Number(e.target.value))}
              className="w-full accent-[#E05638] cursor-pointer"
            />

            <p className="mono-label text-neutral-400 mt-9 mb-3">Gestione SEO</p>
            <div className="flex flex-wrap gap-2.5">
              {SEO.map((x) => (
                <button key={x.id} data-testid={`calc-seo-${x.id}`} onClick={() => setSeo(x.id)} className={chip(seo === x.id)}>
                  {x.label}
                </button>
              ))}
            </div>

            <p className="mono-label text-neutral-400 mt-9 mb-3">Assistenza</p>
            <div className="flex flex-wrap gap-2.5">
              {UPDATES.map((x) => (
                <button key={x.id} data-testid={`calc-updates-${x.id}`} onClick={() => setUpdates(x.id)} className={chip(updates === x.id)}>
                  {x.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-8 sm:p-10 flex flex-col">
            <p className="mono-label text-neutral-400">La tua stima</p>
            <div className="mt-8 flex flex-wrap items-end gap-x-10 gap-y-6">
              <div>
                <p data-testid="calc-onetime" className="font-display font-extrabold text-4xl sm:text-5xl">€{oneTime}</p>
                <p className="text-xs text-neutral-400 mt-2">creazione, una tantum</p>
              </div>
              <div className="pb-1 pl-10 border-l border-white/15">
                <p data-testid="calc-monthly" className="font-display font-extrabold text-4xl sm:text-5xl text-[#E05638]">€{monthly}</p>
                <p className="text-xs text-neutral-400 mt-2">al mese, tutto incluso</p>
              </div>
            </div>

            <div className="mt-9 space-y-2.5 flex-1">
              {[
                [`Base creazione (fino a 3 pagine)`, "€690"],
                [`${pages - 3 > 0 ? `${pages - 3} pagine extra` : "Pagine extra"}`, pages - 3 > 0 ? `€${(pages - 3) * 80}` : "—"],
                [t.label, t.extra > 0 ? `+€${t.extra}` : "incluso"],
                [s.label, s.monthly > 0 ? `+€${s.monthly}/mese` : "—"],
                [u.label, u.monthly > 0 ? `+€${u.monthly}/mese` : "incluso"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between text-sm border-b border-white/10 pb-2.5">
                  <span className="text-neutral-300">{label}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>

            <p className="text-xs text-neutral-500 mt-6 leading-relaxed">
              Stima indicativa e non vincolante: il preventivo finale lo definiamo insieme in call.
            </p>
            <button
              data-testid="calc-cta-button"
              onClick={request}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#E05638] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#C8452A] transition-colors duration-300"
            >
              Richiedi questo preventivo <ArrowRight size={15} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
